import { LOCALE_CATEGORIES } from '@/assets/locales/categories';

import { i18n, loadLocales } from '@/i18n';
import {
  createRouter,
  createWebHistory,
  RouteLocationNormalized,
  RouteRecordRaw,
} from 'vue-router';
import { useToast } from 'vue-toastification';
import { useSessionStore } from '@/domains/-session/stores/useSessionStore.ts';
import { useDataStore } from '@/domains/-data/stores/useDataStore.ts';

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to) {
    if (to.hash === '')
      setTimeout(() => {
        const container = document.querySelector('.ods-content-container > div');
        if (container) container.scrollTop = 0;
      }, 500);
  },
  routes: [
    {
      name: 'home',
      path: '/',
      component: () => import('./Home.vue'),
    },
    {
      name: 'redirector',
      path: '/analysis/:cohortId',
      component: () => import('./Home.vue'),
      meta: {
        hasNav: false,
        requiresAuth: true,
        localeCategories: [LOCALE_CATEGORIES.BENCHMARK, LOCALE_CATEGORIES.SKILL_DEFINITION],
      },
      beforeEnter: async (to) => {
        const guard = await guardUUIDAndLoadData(to.params.cohortId as string);
        if (guard) return guard;

        const dataStore = useDataStore();

        if (dataStore.hasAIGroupData && !dataStore.hasGroupData) {
          //AI group -> redirect to AIAdoption view
          return { name: 'ai_adoption', params: { cohortId: to.params.cohortId } };
        }
        if (!dataStore.hasAIGroupData && !dataStore.hasGroupData && dataStore.hasIndividualData) {
          //Resources group -> redirect to Resources view
          return { name: 'resources', params: { cohortId: to.params.cohortId } };
        }
        if (dataStore.hasGroupData) {
          //Standard group -> redirect to Benchmark view
          return { name: 'dashboard', params: { cohortId: to.params.cohortId } };
        }
      },
    },
    {
      name: 'dashboard',
      path: '/dashboard/:cohortId',
      component: () => import('./domains/-benchmark/views/Benchmark.vue'),
      meta: {
        hasNav: true,
        requiresAuth: true,
        localeCategories: [LOCALE_CATEGORIES.BENCHMARK, LOCALE_CATEGORIES.SKILL_DEFINITION],
      },
      beforeEnter: async (to) => {
        return await guardUUIDAndLoadData(to.params.cohortId as string);
      },
    },
    {
      name: 'resources',
      path: '/resources/:cohortId',
      component: () => import('./domains/-resources/views/Resources.vue'),
      meta: {
        hasNav: true,
        requiresAuth: true,
        localeCategories: [LOCALE_CATEGORIES.RESOURCES],
      },
      beforeEnter: async (to) => {
        return await guardUUIDAndLoadData(to.params.cohortId as string);
      },
    },
    {
      name: 'ai_adoption',
      path: '/ai_adoption/:cohortId',
      component: () => import('./domains/-ai_adoption/views/AiAdoption.vue'),
      meta: {
        hasNav: true,
        requiresAuth: true,
        localeCategories: [
          LOCALE_CATEGORIES.RESOURCES,
          LOCALE_CATEGORIES.BENCHMARK,
          LOCALE_CATEGORIES.SKILL_DEFINITION,
          LOCALE_CATEGORIES.AI_ADOPTION_REPORT,
          LOCALE_CATEGORIES.AI_ADOPTION,
        ],
      },
      beforeEnter: async (to) => {
        return await guardUUIDAndLoadData(to.params.cohortId as string);
      },
    },
  ],
});

async function guardUUIDAndLoadData(cohortId: string) {
  if (cohortId) {
    try {
      await useDataStore().loadData(cohortId as string);
    } catch (error) {
      console.warn(error);
      useToast().error(i18n.global.t('errors.data.global'));
      return { name: 'home' };
    }
  } else return { name: 'home' };
}

export function isExactActiveRoute(routeName: string): boolean {
  return router.currentRoute.value.name === routeName;
}

export function isActiveRoute(routeName: string): boolean {
  return (
    isExactActiveRoute(routeName) ||
    router.currentRoute.value.matched.some((item) => item.name === routeName)
  );
}

router.beforeEach(async (to: RouteLocationNormalized) => {
  const requiresAuth = to.matched.some(
    (record: RouteRecordRaw) =>
      (record.meta && Object.hasOwn(record.meta, 'requiresAuth')) ?? false,
  );
  if (requiresAuth) {
    // Update the session (getMe if not already done)
    const session = useSessionStore();
    await session.loadUser('forceMe' in to.meta && !!to.meta.forceMe);

    // Redirect unauthenticated users to `sign-in`
    if (!session.loaded) return { name: 'sign_in', query: { redirect: to.fullPath } };
  }
  return;
});

router.beforeResolve(async (to: RouteLocationNormalized) => {
  await loadLocales(LOCALE_CATEGORIES.LEXICON);
  await loadLocales(LOCALE_CATEGORIES.ERRORS);
  await loadLocales(LOCALE_CATEGORIES.GENERIC);
  if ('hasNav' in to.meta && to.meta.hasNav === true) {
    await loadLocales(LOCALE_CATEGORIES.NAVIGATION);
  }
  if ('localeCategories' in to.meta) {
    await loadLocales(...(to.meta.localeCategories as LOCALE_CATEGORIES[]));
  }
});

export { router };
export default router;
